import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Layers, Loader2, Maximize2, Minimize2, Pencil, Play, Save, X, ZoomIn, ZoomOut } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { FloorplanZone, StoreProfileDetail } from '../../types';
import type { SpatialEntity } from '../../lib/spatialEntity';
import {
  MAP_LAYER_LABELS,
  MAP_POINT_STYLES,
  buildMapPoints,
  getContextForSelection,
  type MapPoint,
} from '../../lib/retailerViewUtils';
import { cacheBustUrl } from '../../lib/cacheBustUrl';
import { buildZoneOutlineColors } from '../../lib/instoreLocationGroups';
import {
  reassignZoneToInstoreLocation,
  sanitizeFloorplanWritePayload,
} from '../../lib/floorplanZoneUtils';
import { FloorplanCanvas } from '../FloorplanCanvas';
import { RetailerContextPanel } from './RetailerContextPanel';
import { RetailerMapEditPanel } from './RetailerMapEditPanel';
import { RetailerWalkthroughTour } from './RetailerWalkthroughTour';

type LayerKey = keyof typeof MAP_LAYER_LABELS;

interface RetailerStoreMapProps {
  profile: StoreProfileDetail;
  asOf: Date | null;
  selection: { kind: SpatialEntity['kind']; id: string } | null;
  onSelectionChange: (selection: { kind: SpatialEntity['kind']; id: string } | null) => void;
}

const DEFAULT_LAYERS: Record<LayerKey, boolean> = {
  campaigns: true,
  photos: true,
  videos: true,
  media: true,
  issues: false,
};

const RETAILER_LAYER_KEYS: LayerKey[] = ['campaigns', 'photos', 'videos', 'media'];
const RETAILER_POINT_KINDS: MapPoint['kind'][] = ['campaign', 'photo', 'video', 'media'];

function useCompactMapLayout(): boolean {
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 1023px)');
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return compact;
}

export function RetailerStoreMap({
  profile,
  asOf,
  selection,
  onSelectionChange,
}: RetailerStoreMapProps) {
  const [layers, setLayers] = useState<Record<LayerKey, boolean>>(DEFAULT_LAYERS);
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourWalkId, setTourWalkId] = useState<string | undefined>(undefined);
  const [pendingTourWalkId, setPendingTourWalkId] = useState<string | undefined>(undefined);
  const [isEditingMap, setIsEditingMap] = useState(false);
  const [isSavingMap, setIsSavingMap] = useState(false);
  const [draftZones, setDraftZones] = useState<FloorplanZone[]>(profile.floorplanZones);
  const [selectedEditZoneId, setSelectedEditZoneId] = useState<string | null>(null);
  const [redrawZone, setRedrawZone] = useState<FloorplanZone | null>(null);
  const [deletingLocationId, setDeletingLocationId] = useState<number | null>(null);
  const compactMapLayout = useCompactMapLayout();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const floorplanDisplayUrl = cacheBustUrl(profile.floorplanImageUrl, profile.floorplanImageVersion);
  const displayedZones = isEditingMap ? draftZones : profile.floorplanZones;
  const zoneOutlineColors = useMemo(
    () => buildZoneOutlineColors(displayedZones),
    [displayedZones],
  );
  const mapPoints = useMemo(
    () => buildMapPoints(profile, asOf, layers),
    [profile, asOf, layers],
  );
  const context = useMemo(
    () => getContextForSelection(profile, selection, asOf),
    [profile, selection, asOf],
  );
  const contextKey = selection ? `${selection.kind}:${selection.id}` : 'empty';
  const selectedEditZone = draftZones.find((zone) => zone.id === selectedEditZoneId) ?? null;
  const storeLocationOptions = useMemo(
    () => profile.storeInstoreLocations ?? profile.retailerInstoreLocations ?? [],
    [profile.retailerInstoreLocations, profile.storeInstoreLocations],
  );
  const unavailableLocationIds = useMemo(() => new Set(
    draftZones
      .filter((zone) => zone.id !== selectedEditZoneId && zone.instoreLocationId != null)
      .map((zone) => zone.instoreLocationId as number),
  ), [draftZones, selectedEditZoneId]);

  useEffect(() => {
    if (isEditingMap) return;
    setDraftZones(profile.floorplanZones);
  }, [isEditingMap, profile.floorplanZones]);

  useEffect(() => {
    if (!pendingTourWalkId || contextOpen) return;
    // Let the location sheet finish leaving before mounting the tour dialog.
    // Stacking Radix portals here caused the removeChild crash seen in review.
    const timeoutId = window.setTimeout(() => {
      setTourWalkId(pendingTourWalkId);
      setPendingTourWalkId(undefined);
      setTourOpen(true);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [contextOpen, pendingTourWalkId]);

  const handleAreaClick = (
    kind: SpatialEntity['kind'],
    id: string,
    options: { toggle?: boolean } = {},
  ) => {
    const shouldToggle = options.toggle ?? true;
    const next = shouldToggle && selection?.kind === kind && selection.id === id
      ? null
      : { kind, id };
    onSelectionChange(next);
    const nextContext = getContextForSelection(profile, next, asOf);
    setContextOpen(Boolean(next && nextContext && compactMapLayout));
  };

  const beginMapEdit = () => {
    setDraftZones(profile.floorplanZones);
    setSelectedEditZoneId(null);
    setRedrawZone(null);
    onSelectionChange(null);
    setContextOpen(false);
    setIsEditingMap(true);
  };

  const cancelMapEdit = () => {
    setDraftZones(profile.floorplanZones);
    setSelectedEditZoneId(null);
    setRedrawZone(null);
    setContextOpen(false);
    setIsEditingMap(false);
  };

  const updateDraftZone = (zoneId: string, patch: Partial<FloorplanZone>) => {
    setDraftZones((current) => current.map((zone) => (
      zone.id === zoneId ? { ...zone, ...patch } : zone
    )));
  };

  const removeDraftZone = (zoneId: string) => {
    setDraftZones((current) => current.filter((zone) => zone.id !== zoneId));
    setSelectedEditZoneId(null);
    setContextOpen(false);
  };

  const startRedraw = (zone: FloorplanZone) => {
    setRedrawZone(zone);
    setDraftZones((current) => current.filter((item) => item.id !== zone.id));
    setSelectedEditZoneId(null);
    setContextOpen(false);
  };

  const deleteRetailerLocation = async (zone: FloorplanZone) => {
    if (zone.instoreLocationId == null) return;
    const locationId = zone.instoreLocationId;
    setDeletingLocationId(locationId);
    try {
      await apiRequest(`/api/admin/instore-locations/${locationId}`, 'DELETE');
      setDraftZones((current) => current.filter(
        (item) => item.instoreLocationId !== locationId,
      ));
      setSelectedEditZoneId(null);
      setContextOpen(false);
      await queryClient.invalidateQueries({
        queryKey: [`/api/admin/instore-locations?retailerId=${profile.retailerId}`],
      });
      toast({
        title: 'Store location deleted',
        description: 'Its shape has been removed. Save the map to keep this floorplan change.',
      });
    } catch (error) {
      toast({
        title: 'Could not delete store location',
        description: error instanceof Error ? error.message : 'Delete failed',
        variant: 'destructive',
      });
    } finally {
      setDeletingLocationId(null);
    }
  };

  const saveMap = async () => {
    if (profile.storeId == null) return;
    setIsSavingMap(true);
    try {
      const payload = sanitizeFloorplanWritePayload({
        aisles: profile.aisles as unknown as Array<Record<string, unknown>>,
        zones: draftZones.map((zone) => ({
          ...zone,
          outlineColor: zone.outlineColor ?? zoneOutlineColors[zone.id],
        })) as unknown as Array<Record<string, unknown>>,
      });
      const response = await apiRequest(`/api/store-profiles/${profile.storeId}/floorplan`, 'PUT', payload);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save store map');
      }
      const storeIdParam = String(profile.storeId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
      ]);
      setSelectedEditZoneId(null);
      setRedrawZone(null);
      setContextOpen(false);
      setIsEditingMap(false);
      toast({ title: 'Store map updated' });
    } catch (error) {
      toast({
        title: 'Could not save store map',
        description: error instanceof Error ? error.message : 'Update failed',
        variant: 'destructive',
      });
    } finally {
      setIsSavingMap(false);
    }
  };

  const startTour = (walkthroughId?: string) => {
    const requested = walkthroughId
      ? profile.walkthroughs.find((walk) => walk.id === walkthroughId)
      : undefined;
    const preferred = requested ?? profile.walkthroughs.find((walk) =>
      walk.title.toLowerCase().includes('aisle 3') || (walk.aisleNumbers ?? []).includes(3),
    ) ?? profile.walkthroughs[0];
    if (!preferred) return;
    // Avoid stacking the tour dialog on top of the mobile location sheet.
    setPendingTourWalkId(preferred.id);
    setContextOpen(false);
  };

  const handleTourFocusArea = useCallback((
    kind: SpatialEntity['kind'],
    id: string,
  ) => {
    onSelectionChange({ kind, id });
    // The tour owns the foreground while it is open. Keep the floorplan
    // selection in sync without reopening the location sheet on top.
    setContextOpen(false);
  }, [onSelectionChange]);

  const mapShellClass = fullscreen
    ? 'fixed inset-0 z-50 bg-white p-3 flex flex-col'
    : 'relative';

  return (
    <>
      <div className={mapShellClass}>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {isEditingMap ? (
            <>
              <Button type="button" size="sm" onClick={() => void saveMap()} disabled={isSavingMap || redrawZone != null}>
                {isSavingMap ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save map
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={cancelMapEdit} disabled={isSavingMap}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              {redrawZone && (
                <span className="text-sm text-blue-700">
                  Draw a new area for {redrawZone.label}
                </span>
              )}
            </>
          ) : (
            <>
              <Button type="button" size="sm" onClick={() => startTour()} disabled={profile.walkthroughs.length === 0}>
                <Play className="h-4 w-4 mr-1.5" aria-hidden />
                Take a store tour
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={beginMapEdit}
                disabled={profile.storeId == null || profile.floorplanZones.length === 0}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit map
              </Button>
            </>
          )}

          <div className={`hidden md:flex flex-wrap items-center gap-3 ml-auto ${isEditingMap ? 'opacity-40 pointer-events-none' : ''}`} role="group" aria-label="Map layers">
            {RETAILER_LAYER_KEYS.map((key) => (
              <label key={key} className="flex items-center gap-1.5 text-sm cursor-pointer">
                <Checkbox
                  checked={layers[key]}
                  onCheckedChange={() => setLayers((current) => ({ ...current, [key]: !current[key] }))}
                />
                {MAP_LAYER_LABELS[key]}
              </label>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="md:hidden ml-auto"
            onClick={() => setLayersOpen(true)}
          >
            <Layers className="h-4 w-4 mr-1" aria-hidden />
            Layers
          </Button>

          <div className="flex items-center gap-1">
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setZoom((z) => Math.min(z + 0.2, 2))} aria-label="Zoom in">
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => setZoom((z) => Math.max(z - 0.2, 0.6))} aria-label="Zoom out">
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setFullscreen((value) => !value)}
              aria-label={fullscreen ? 'Exit full screen map' : 'Full screen map'}
            >
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-3 ${fullscreen ? 'flex-1 min-h-0' : ''}`}>
          <div className={`rounded-xl border bg-white p-2 min-w-0 ${fullscreen ? 'min-h-0 flex flex-col' : ''}`}>
            <div className={`relative ${fullscreen ? 'flex-1 min-h-0' : 'min-h-[420px] lg:min-h-[560px]'}`}>
              {profile.storeAreasCount === 0 && !profile.floorplanImageUrl ? (
                <div className="h-full flex items-center justify-center text-center text-gray-500 p-6">
                  <div>
                    <p className="font-medium">Store map not ready yet</p>
                    <p className="text-sm mt-1">An administrator needs to add the floorplan and store areas.</p>
                  </div>
                </div>
              ) : (
                <FloorplanCanvas
                  zones={displayedZones}
                  aisles={profile.aisles}
                  imageUrl={floorplanDisplayUrl}
                  selectedAisleId={!isEditingMap && selection?.kind === 'aisle' ? selection.id : null}
                  selectedZoneId={isEditingMap
                    ? selectedEditZoneId
                    : selection?.kind === 'zone'
                      ? selection.id
                      : null}
                  onAisleClick={isEditingMap ? undefined : (aisleId) => handleAreaClick('aisle', aisleId)}
                  onZoneClick={(zoneId) => {
                    if (isEditingMap) {
                      setSelectedEditZoneId(zoneId);
                      setRedrawZone(null);
                      setContextOpen(compactMapLayout);
                      return;
                    }
                    handleAreaClick('zone', zoneId);
                  }}
                  editable={isEditingMap}
                  onZoneMove={isEditingMap ? (zoneId, patch) => updateDraftZone(zoneId, patch) : undefined}
                  onZoneResize={isEditingMap ? (zoneId, patch) => updateDraftZone(zoneId, patch) : undefined}
                  onZoneCreate={isEditingMap && redrawZone
                    ? (bounds) => {
                      setDraftZones((current) => [...current, { ...redrawZone, ...bounds }]);
                      setSelectedEditZoneId(redrawZone.id);
                      setRedrawZone(null);
                      setContextOpen(compactMapLayout);
                    }
                    : undefined}
                  mediaLocations={[]}
                  showMedia={false}
                  zoom={zoom}
                  zoneOutlineColors={zoneOutlineColors}
                  minimalOverlays
                  drawHint={redrawZone ? `Draw a new area for ${redrawZone.label}` : undefined}
                  mapOverlayPoints={isEditingMap ? [] : mapPoints}
                  onMapPointClick={isEditingMap
                    ? undefined
                    : (point) => handleAreaClick(
                      point.spatialEntityKind,
                      point.spatialEntityId,
                      { toggle: false },
                    )}
                />
              )}
            </div>

            <div className="flex flex-wrap gap-3 px-2 py-2 border-t text-xs text-gray-600">
              {RETAILER_POINT_KINDS.map((kind) => (
                <span key={kind} className="inline-flex items-center gap-1.5">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: MAP_POINT_STYLES[kind].fill }}
                    aria-hidden
                  />
                  {MAP_POINT_STYLES[kind].label}
                </span>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            {isEditingMap ? (
              <RetailerMapEditPanel
                zone={selectedEditZone}
                colour={selectedEditZone ? zoneOutlineColors[selectedEditZone.id] ?? null : null}
                retailerId={profile.retailerId}
                locations={storeLocationOptions}
                unavailableLocationIds={unavailableLocationIds}
                isDeletingLocation={selectedEditZone?.instoreLocationId === deletingLocationId}
                onUpdate={(patch) => {
                  if (selectedEditZone) updateDraftZone(selectedEditZone.id, patch);
                }}
                onLocationChange={(location) => {
                  if (!selectedEditZone) return;
                  const reassigned = reassignZoneToInstoreLocation(selectedEditZone, location);
                  updateDraftZone(selectedEditZone.id, reassigned);
                }}
                onRedraw={() => {
                  if (selectedEditZone) startRedraw(selectedEditZone);
                }}
                onRemove={() => {
                  if (selectedEditZone) removeDraftZone(selectedEditZone.id);
                }}
                onDeleteLocation={() => {
                  if (selectedEditZone) void deleteRetailerLocation(selectedEditZone);
                }}
              />
            ) : (
              <RetailerContextPanel
                key={contextKey}
                context={context}
                className="h-full"
                onOpenWalkthrough={startTour}
              />
            )}
          </div>
        </div>
      </div>

      <Sheet
        open={contextOpen && compactMapLayout}
        onOpenChange={setContextOpen}
      >
        <SheetContent side="bottom" className="lg:hidden max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white">
          <SheetHeader>
            <SheetTitle>
              {isEditingMap ? selectedEditZone?.label ?? 'Edit store map' : context?.locationName ?? 'Location details'}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            {isEditingMap ? (
              <RetailerMapEditPanel
                zone={selectedEditZone}
                colour={selectedEditZone ? zoneOutlineColors[selectedEditZone.id] ?? null : null}
                retailerId={profile.retailerId}
                locations={storeLocationOptions}
                unavailableLocationIds={unavailableLocationIds}
                isDeletingLocation={selectedEditZone?.instoreLocationId === deletingLocationId}
                onUpdate={(patch) => {
                  if (selectedEditZone) updateDraftZone(selectedEditZone.id, patch);
                }}
                onLocationChange={(location) => {
                  if (!selectedEditZone) return;
                  const reassigned = reassignZoneToInstoreLocation(selectedEditZone, location);
                  updateDraftZone(selectedEditZone.id, reassigned);
                }}
                onRedraw={() => {
                  if (selectedEditZone) startRedraw(selectedEditZone);
                }}
                onRemove={() => {
                  if (selectedEditZone) removeDraftZone(selectedEditZone.id);
                }}
                onDeleteLocation={() => {
                  if (selectedEditZone) void deleteRetailerLocation(selectedEditZone);
                }}
              />
            ) : (
              <RetailerContextPanel
                key={contextKey}
                context={context}
                className="border-0 shadow-none max-h-none"
                onOpenWalkthrough={startTour}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={layersOpen} onOpenChange={setLayersOpen}>
        <SheetContent side="bottom" className="md:hidden rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>Map layers</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {RETAILER_LAYER_KEYS.map((key) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={layers[key]}
                  onCheckedChange={() => setLayers((current) => ({ ...current, [key]: !current[key] }))}
                />
                {MAP_LAYER_LABELS[key]}
              </label>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <RetailerWalkthroughTour
        profile={profile}
        open={tourOpen}
        onOpenChange={setTourOpen}
        initialWalkId={tourWalkId}
        onFocusArea={handleTourFocusArea}
      />
    </>
  );
}
